import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../utils/api';
import toast from 'react-hot-toast';
import { useAuth } from '../contexts/AuthContext';

const FEED_TYPES = [
  'Doré',
  'Industrial scrap',
  'Jewelry scrap',
];

// Countries ordered alphabetically; "Other" sentinel at end
const COUNTRIES = [
  'Argentina','Australia','Austria','Belgium','Bolivia','Brazil','Canada',
  'Chile','China','Colombia','Czech Republic','Denmark','Ecuador','Egypt',
  'Finland','France','Germany','Ghana','Greece','Hungary','India','Indonesia',
  'Ireland','Italy','Japan','Kazakhstan','Malaysia','Mexico','Morocco',
  'Netherlands','New Zealand','Norway','Peru','Philippines','Poland',
  'Portugal','Romania','Russia','Saudi Arabia','Singapore','South Africa',
  'South Korea','Spain','Sweden','Switzerland','Taiwan','Thailand','Turkey',
  'UAE','United Kingdom','United States','Vietnam','Zimbabwe','Other',
];

/**
 * Regional benchmarks keyed by country name.
 * electricity_cost_usd_per_kwh  — IEA / GlobalPetrolPrices.com industrial rate
 * labor_cost_usd_per_hour       — ILO / World Bank manufacturing benchmark
 * Fields not listed default to global values.
 */
const COUNTRY_BENCHMARKS = {
  Argentina:        { electricity: 0.06, labor: 7  },
  Australia:        { electricity: 0.10, labor: 38 },
  Austria:          { electricity: 0.14, labor: 30 },
  Belgium:          { electricity: 0.16, labor: 32 },
  Bolivia:          { electricity: 0.06, labor: 4  },
  Brazil:           { electricity: 0.12, labor: 8  },
  Canada:           { electricity: 0.08, labor: 30 },
  Chile:            { electricity: 0.11, labor: 12 },
  China:            { electricity: 0.08, labor: 8  },
  Colombia:         { electricity: 0.09, labor: 5  },
  'Czech Republic': { electricity: 0.13, labor: 15 },
  Denmark:          { electricity: 0.18, labor: 35 },
  Ecuador:          { electricity: 0.07, labor: 4  },
  Egypt:            { electricity: 0.04, labor: 3  },
  Finland:          { electricity: 0.12, labor: 32 },
  France:           { electricity: 0.13, labor: 28 },
  Germany:          { electricity: 0.22, labor: 32 },
  Ghana:            { electricity: 0.08, labor: 3  },
  Greece:           { electricity: 0.14, labor: 18 },
  Hungary:          { electricity: 0.12, labor: 12 },
  India:            { electricity: 0.09, labor: 4  },
  Indonesia:        { electricity: 0.08, labor: 4  },
  Ireland:          { electricity: 0.19, labor: 28 },
  Italy:            { electricity: 0.17, labor: 24 },
  Japan:            { electricity: 0.17, labor: 25 },
  Kazakhstan:       { electricity: 0.05, labor: 6  },
  Malaysia:         { electricity: 0.06, labor: 6  },
  Mexico:           { electricity: 0.09, labor: 6  },
  Morocco:          { electricity: 0.11, labor: 4  },
  Netherlands:      { electricity: 0.16, labor: 30 },
  'New Zealand':    { electricity: 0.10, labor: 28 },
  Norway:           { electricity: 0.06, labor: 40 },
  Peru:             { electricity: 0.07, labor: 5  },
  Philippines:      { electricity: 0.14, labor: 4  },
  Poland:           { electricity: 0.14, labor: 12 },
  Portugal:         { electricity: 0.13, labor: 16 },
  Romania:          { electricity: 0.11, labor: 10 },
  Russia:           { electricity: 0.05, labor: 8  },
  'Saudi Arabia':   { electricity: 0.05, labor: 12 },
  Singapore:        { electricity: 0.14, labor: 22 },
  'South Africa':   { electricity: 0.08, labor: 7  },
  'South Korea':    { electricity: 0.10, labor: 18 },
  Spain:            { electricity: 0.14, labor: 20 },
  Sweden:           { electricity: 0.09, labor: 32 },
  Switzerland:      { electricity: 0.14, labor: 40 },
  Taiwan:           { electricity: 0.09, labor: 15 },
  Thailand:         { electricity: 0.10, labor: 7  },
  Turkey:           { electricity: 0.11, labor: 8  },
  UAE:              { electricity: 0.08, labor: 12 },
  'United Kingdom': { electricity: 0.24, labor: 25 },
  'United States':  { electricity: 0.09, labor: 30 },
  Vietnam:          { electricity: 0.08, labor: 4  },
  Zimbabwe:         { electricity: 0.09, labor: 3  },
  Other:            { electricity: 0.10, labor: 25 },
};

const STEPS = ['Customer', 'Process', 'Economics'];

const inputStyle = {
  width: '100%',
  padding: '0.5rem 0.75rem',
  fontSize: '0.875rem',
  color: 'var(--color-text-primary)',
  background: '#fff',
  border: '1px solid var(--color-border)',
  borderRadius: 'var(--radius-md)',
  boxSizing: 'border-box',
};

const focusStyle = {
  outline: 'none',
  borderColor: 'var(--color-blue)',
  boxShadow: '0 0 0 3px rgba(62,104,178,0.15)',
};

function Field({ label, required, hint, children }) {
  return (
    <div style={{ marginBottom: '1.25rem' }}>
      <label style={{ display: 'block', fontSize: '0.8125rem', fontWeight: 600, color: 'var(--color-text-primary)', marginBottom: '0.375rem' }}>
        {label}{required && <span style={{ color: '#E53E3E', marginLeft: 2 }}>*</span>}
      </label>
      {children}
      {hint && <p style={{ fontSize: '0.75rem', color: 'var(--color-text-muted)', marginTop: '0.25rem' }}>{hint}</p>}
    </div>
  );
}

function Input({ value, onChange, type = 'text', placeholder, min, max, step, autoFocus }) {
  const [focused, setFocused] = useState(false);
  return (
    <input
      type={type}
      value={value}
      onChange={e => onChange(e.target.value)}
      placeholder={placeholder}
      min={min}
      max={max}
      step={step}
      autoFocus={autoFocus}
      style={{ ...inputStyle, ...(focused ? focusStyle : {}) }}
      onFocus={() => setFocused(true)}
      onBlur={() => setFocused(false)}
    />
  );
}

function Select({ value, onChange, options }) {
  const [focused, setFocused] = useState(false);
  return (
    <select
      value={value}
      onChange={e => onChange(e.target.value)}
      style={{ ...inputStyle, appearance: 'none', backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='8' viewBox='0 0 12 8'%3E%3Cpath d='M1 1l5 5 5-5' stroke='%23718096' strokeWidth='1.5' fill='none' strokeLinecap='round'/%3E%3C/svg%3E")`, backgroundRepeat: 'no-repeat', backgroundPosition: 'right 0.75rem center', paddingRight: '2rem', ...(focused ? focusStyle : {}) }}
      onFocus={() => setFocused(true)}
      onBlur={() => setFocused(false)}
    >
      <option value="">— Select —</option>
      {options.map(o => <option key={o} value={o}>{o}</option>)}
    </select>
  );
}

function TwoCol({ children }) {
  return (
    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
      {children}
    </div>
  );
}

function ThreeCol({ children }) {
  return (
    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '1rem' }}>
      {children}
    </div>
  );
}

function SectionTitle({ children }) {
  return (
    <div style={{ margin: '1.5rem 0 1rem', borderBottom: '2px solid var(--color-blue)', paddingBottom: '0.4rem' }}>
      <span style={{ fontSize: '0.8125rem', fontWeight: 700, color: 'var(--color-dark-blue)', textTransform: 'uppercase', letterSpacing: '0.04em' }}>
        {children}
      </span>
    </div>
  );
}

export default function Assessment() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [step, setStep] = useState(0);
  const [submitting, setSubmitting] = useState(false);

  // Verified Constants
  const [constants, setConstants] = useState([]);
  const [editingKey, setEditingKey] = useState(null);
  const [editValue, setEditValue] = useState('');
  const [savingKey, setSavingKey] = useState(null);

  const [form, setForm] = useState({
    customer_name: '',
    contact_person: '',
    country: '',
    application: '',
    daily_ag_production_kg_per_day: '',
    feed_type: '',
    feed_pct_ag: '',
    feed_pct_cu: '',
    feed_pct_au: '',
    // Block 2 — Financial & Operational (defaults from live market data, 12 May 2026)
    silver_price_usd_per_troy_oz: '84.79',   // goldprice.org / BullionVault spot
    gold_price_usd_per_troy_oz: '4723',       // goldprice.org / BullionVault spot
    copper_price_usd_per_tonne: '14304',      // LME via Yahoo Finance ($6.49/lb × 2204.62)
    silver_lease_rate_pct_per_year: '9',       // Annual silver financing / lease rate
    electricity_cost_usd_per_kwh: '0.10',     // IEA global industrial average
    labor_cost_usd_per_hour: '25',            // mid-range global industrial benchmark
    nitric_acid_cost_usd_per_ltr: '0.50',     // industrial grade HNO3 benchmark
    operating_days_per_year: '250',
    customer_payback_years: '3',
    // Block 3 — Existing Installation
    moebius_age_years: '',
    monthly_repair_cost_usd: '',
    downtime_weeks_per_year: '',
    upcoming_major_spend_usd: '',
    capex_usd: '',
    notes: '',
  });

  const set = (field) => (val) => setForm(f => ({ ...f, [field]: val }));

  // Fetch verified constants on mount
  useEffect(() => {
    api.get('/constants')
      .then(r => setConstants(r.data.data))
      .catch(() => {}); // non-critical; silently skip if unavailable
  }, []);

  async function saveConstant(key) {
    const parsed = parseFloat(editValue);
    if (isNaN(parsed)) { toast.error('Enter a valid number'); return; }
    setSavingKey(key);
    try {
      const res = await api.put(`/constants/${key}`, { value: parsed });
      setConstants(cs => cs.map(c => c.key === key ? res.data.data : c));
      setEditingKey(null);
      toast.success('Constant updated');
    } catch {
      toast.error('Failed to update constant');
    } finally {
      setSavingKey(null);
    }
  }

  // Auto-fill repair cost default when Moebius age changes
  useEffect(() => {
    const age = parseFloat(form.moebius_age_years);
    if (isNaN(age)) return;
    let defaultRepair = '';
    if (age === 0)        defaultRepair = '0';
    else if (age <= 5)   defaultRepair = '2000';
    else if (age <= 10)  defaultRepair = '3000';
    else if (age <= 15)  defaultRepair = '5000';
    else                 defaultRepair = '8000';
    setForm(f => ({ ...f, monthly_repair_cost_usd: defaultRepair }));
  }, [form.moebius_age_years]); // eslint-disable-line

  // Auto-fill regional benchmarks when country changes
  useEffect(() => {
    if (!form.country) return;
    const b = COUNTRY_BENCHMARKS[form.country];
    if (!b) return;
    setForm(f => ({
      ...f,
      electricity_cost_usd_per_kwh: String(b.electricity),
      labor_cost_usd_per_hour:      String(b.labor),
    }));
  }, [form.country]); // eslint-disable-line

  function validateStep() {
    if (step === 0) {
      if (!form.customer_name.trim()) { toast.error('Company name is required'); return false; }
      if (!form.daily_ag_production_kg_per_day || isNaN(parseFloat(form.daily_ag_production_kg_per_day)) || parseFloat(form.daily_ag_production_kg_per_day) <= 0) {
        toast.error('Daily Ag Production is required'); return false;
      }
      if (!form.feed_type) { toast.error('Feed Type is required'); return false; }
      if (form.feed_pct_ag === '' || isNaN(parseFloat(form.feed_pct_ag))) { toast.error('Feed % Ag is required'); return false; }
      if (form.feed_pct_cu === '' || isNaN(parseFloat(form.feed_pct_cu))) { toast.error('Feed % Cu is required'); return false; }
      if (form.feed_pct_au === '' || isNaN(parseFloat(form.feed_pct_au))) { toast.error('Feed % Au is required'); return false; }
      const ag = parseFloat(form.feed_pct_ag);
      const cu = parseFloat(form.feed_pct_cu);
      const au = parseFloat(form.feed_pct_au);
      if (ag + cu + au > 100) {
        toast.error('Feed % Ag + % Au + % Cu must be ≤ 100%');
        return false;
      }
      // Block 2 validation
      if (!form.silver_price_usd_per_troy_oz || isNaN(parseFloat(form.silver_price_usd_per_troy_oz))) { toast.error('Silver Price is required'); return false; }
      if (!form.gold_price_usd_per_troy_oz || isNaN(parseFloat(form.gold_price_usd_per_troy_oz))) { toast.error('Gold Price is required'); return false; }
      if (!form.copper_price_usd_per_tonne || isNaN(parseFloat(form.copper_price_usd_per_tonne))) { toast.error('Copper Price is required'); return false; }
      if (!form.silver_lease_rate_pct_per_year || isNaN(parseFloat(form.silver_lease_rate_pct_per_year))) { toast.error('Silver Financing / Lease Rate is required'); return false; }
      if (!form.electricity_cost_usd_per_kwh || isNaN(parseFloat(form.electricity_cost_usd_per_kwh))) { toast.error('Electricity Cost is required'); return false; }
      if (!form.labor_cost_usd_per_hour || isNaN(parseFloat(form.labor_cost_usd_per_hour))) { toast.error('Labor Cost is required'); return false; }
      if (!form.nitric_acid_cost_usd_per_ltr || isNaN(parseFloat(form.nitric_acid_cost_usd_per_ltr))) { toast.error('Nitric Acid Cost is required'); return false; }
      if (!form.operating_days_per_year || isNaN(parseFloat(form.operating_days_per_year))) { toast.error('Operating Days / Year is required'); return false; }
      if (!form.customer_payback_years || isNaN(parseFloat(form.customer_payback_years))) { toast.error('Payback Expectation is required'); return false; }
      // Block 3 validation — only age is mandatory
      if (form.moebius_age_years === '' || isNaN(parseFloat(form.moebius_age_years)) || parseFloat(form.moebius_age_years) < 0) {
        toast.error('Age of existing Moebius cells is required (enter 0 for greenfield)'); return false;
      }
    }
    if (step === 2) {
      if (!form.silver_price_usd_per_troy_oz || isNaN(parseFloat(form.silver_price_usd_per_troy_oz))) {
        toast.error('Silver price is required');
        return false;
      }
    }
    return true;
  }

  function next() {
    if (validateStep()) setStep(s => s + 1);
  }

  async function submit() {
    if (!validateStep()) return;
    setSubmitting(true);
    try {
      const payload = {
        app_type: 'moebius_replacement',
        customer_name: form.customer_name.trim(),
        contact_person: form.contact_person.trim() || undefined,
        country: form.country.trim() || undefined,
        application: form.application || undefined,
        daily_ag_production_kg_per_day: form.daily_ag_production_kg_per_day ? parseFloat(form.daily_ag_production_kg_per_day) : undefined,
        feed_type: form.feed_type || undefined,
        feed_pct_ag: form.feed_pct_ag ? parseFloat(form.feed_pct_ag) : undefined,
        feed_pct_cu: form.feed_pct_cu ? parseFloat(form.feed_pct_cu) : undefined,
        feed_pct_au: form.feed_pct_au ? parseFloat(form.feed_pct_au) : undefined,
        silver_price_usd_per_troy_oz: parseFloat(form.silver_price_usd_per_troy_oz),
        gold_price_usd_per_troy_oz: form.gold_price_usd_per_troy_oz ? parseFloat(form.gold_price_usd_per_troy_oz) : undefined,
        copper_price_usd_per_tonne: form.copper_price_usd_per_tonne ? parseFloat(form.copper_price_usd_per_tonne) : undefined,
        silver_lease_rate_pct_per_year: form.silver_lease_rate_pct_per_year ? parseFloat(form.silver_lease_rate_pct_per_year) : undefined,
        electricity_cost_usd_per_kwh: form.electricity_cost_usd_per_kwh ? parseFloat(form.electricity_cost_usd_per_kwh) : undefined,
        labor_cost_usd_per_hour: form.labor_cost_usd_per_hour ? parseFloat(form.labor_cost_usd_per_hour) : undefined,
        nitric_acid_cost_usd_per_ltr: form.nitric_acid_cost_usd_per_ltr ? parseFloat(form.nitric_acid_cost_usd_per_ltr) : undefined,
        customer_payback_years: form.customer_payback_years ? parseFloat(form.customer_payback_years) : undefined,
        moebius_age_years: parseFloat(form.moebius_age_years),
        monthly_repair_cost_usd: form.monthly_repair_cost_usd !== '' ? parseFloat(form.monthly_repair_cost_usd) : undefined,
        downtime_weeks_per_year: form.downtime_weeks_per_year !== '' ? parseFloat(form.downtime_weeks_per_year) : undefined,
        upcoming_major_spend_usd: form.upcoming_major_spend_usd !== '' ? parseFloat(form.upcoming_major_spend_usd) : undefined,
        notes: form.notes.trim() || undefined,
        operating_days_per_year: parseFloat(form.operating_days_per_year),
        capex_usd: form.capex_usd ? parseFloat(form.capex_usd) : undefined,
      };
      const res = await api.post('/assessment', payload);
      navigate(`/assessment/${res.data.data.id}`);
    } catch {
      // error handled by interceptor
    } finally {
      setSubmitting(false);
    }
  }

  const stepDotStyle = (i) => ({
    width: 28, height: 28, borderRadius: '50%',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    fontSize: '0.8125rem', fontWeight: 700,
    background: i < step ? 'var(--color-green)' : i === step ? 'var(--color-blue)' : 'var(--color-border)',
    color: i <= step ? '#fff' : 'var(--color-text-muted)',
  });

  return (
    <div style={{ maxWidth: 680, margin: '2rem auto', padding: '0 1rem' }}>
      {/* Step indicator */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 0, marginBottom: '2rem' }}>
        {STEPS.map((label, i) => (
          <React.Fragment key={i}>
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '0.25rem' }}>
              <div style={stepDotStyle(i)}>
                {i < step ? '✓' : i + 1}
              </div>
              <span style={{ fontSize: '0.75rem', fontWeight: i === step ? 600 : 400, color: i === step ? 'var(--color-dark-blue)' : 'var(--color-text-muted)' }}>
                {label}
              </span>
            </div>
            {i < STEPS.length - 1 && (
              <div style={{ flex: 1, height: 2, background: i < step ? 'var(--color-green)' : 'var(--color-border)', margin: '0 0.5rem', marginBottom: '1.25rem' }} />
            )}
          </React.Fragment>
        ))}
      </div>

      <div className="card" style={{ padding: '2rem' }}>
        <h2 style={{ marginBottom: '0.25rem' }}>New Value Assessment</h2>
        <p style={{ color: 'var(--color-text-secondary)', fontSize: '0.875rem', marginBottom: '1.75rem' }}>
          {step === 0 && 'Enter the customer or prospect details.'}
          {step === 1 && 'Enter the silver process parameters.'}
          {step === 2 && 'Enter economic inputs to calculate ROI.'}
        </p>

        {/* Step 0 — Customer */}
        {step === 0 && (
          <>
            <Field label="Company Name" required>
              <Input value={form.customer_name} onChange={set('customer_name')} placeholder="e.g. Acme Silver Plating" autoFocus />
            </Field>
            <TwoCol>
              <Field label="Contact Person">
                <Input value={form.contact_person} onChange={set('contact_person')} placeholder="e.g. Jane Smith" />
              </Field>
              <Field label="Country">
                <Select value={form.country} onChange={set('country')} options={COUNTRIES} />
              </Field>
            </TwoCol>
            <SectionTitle>Block 1 – Feed and Production Inputs</SectionTitle>

            <Field label="Daily Ag Production" required hint="Total silver produced per day">
              <div style={{ position: 'relative' }}>
                <Input value={form.daily_ag_production_kg_per_day} onChange={set('daily_ag_production_kg_per_day')} type="number" min="0" step="0.1" placeholder="e.g. 10" />
                <span style={{ position: 'absolute', right: '0.75rem', top: '50%', transform: 'translateY(-50%)', fontSize: '0.8rem', color: 'var(--color-text-muted)', pointerEvents: 'none' }}>kg/day</span>
              </div>
            </Field>

            <Field label="Feed Type" required>
              <Select value={form.feed_type} onChange={set('feed_type')} options={FEED_TYPES} />
            </Field>

            <ThreeCol>
              <Field label="Feed % Ag" required hint="Silver content">
                <div style={{ position: 'relative' }}>
                  <Input value={form.feed_pct_ag} onChange={set('feed_pct_ag')} type="number" min="0" max="100" step="0.1" placeholder="e.g. 80" />
                  <span style={{ position: 'absolute', right: '0.75rem', top: '50%', transform: 'translateY(-50%)', fontSize: '0.8rem', color: 'var(--color-text-muted)', pointerEvents: 'none' }}>%</span>
                </div>
              </Field>
              <Field label="Feed % Cu" required hint="Copper content">
                <div style={{ position: 'relative' }}>
                  <Input value={form.feed_pct_cu} onChange={set('feed_pct_cu')} type="number" min="0" max="100" step="0.1" placeholder="e.g. 10" />
                  <span style={{ position: 'absolute', right: '0.75rem', top: '50%', transform: 'translateY(-50%)', fontSize: '0.8rem', color: 'var(--color-text-muted)', pointerEvents: 'none' }}>%</span>
                </div>
              </Field>
              <Field label="Feed % Au" required hint="Gold content">
                <div style={{ position: 'relative' }}>
                  <Input value={form.feed_pct_au} onChange={set('feed_pct_au')} type="number" min="0" max="100" step="0.1" placeholder="e.g. 2" />
                  <span style={{ position: 'absolute', right: '0.75rem', top: '50%', transform: 'translateY(-50%)', fontSize: '0.8rem', color: 'var(--color-text-muted)', pointerEvents: 'none' }}>%</span>
                </div>
              </Field>
            </ThreeCol>

            <SectionTitle>Block 2 – Financial and Operational Inputs</SectionTitle>

            <ThreeCol>
              <Field label="Silver Price" required hint="Source: goldprice.org spot">
                <div style={{ position: 'relative' }}>
                  <Input value={form.silver_price_usd_per_troy_oz} onChange={set('silver_price_usd_per_troy_oz')} type="number" min="0" step="0.01" placeholder="84.79" />
                  <span style={{ position: 'absolute', right: '0.75rem', top: '50%', transform: 'translateY(-50%)', fontSize: '0.8rem', color: 'var(--color-text-muted)', pointerEvents: 'none' }}>$/tr oz</span>
                </div>
              </Field>
              <Field label="Gold Price" required hint="Source: goldprice.org spot">
                <div style={{ position: 'relative' }}>
                  <Input value={form.gold_price_usd_per_troy_oz} onChange={set('gold_price_usd_per_troy_oz')} type="number" min="0" step="1" placeholder="4723" />
                  <span style={{ position: 'absolute', right: '0.75rem', top: '50%', transform: 'translateY(-50%)', fontSize: '0.8rem', color: 'var(--color-text-muted)', pointerEvents: 'none' }}>$/tr oz</span>
                </div>
              </Field>
              <Field label="Copper Price" required hint="Source: LME cash ($/tonne)">
                <div style={{ position: 'relative' }}>
                  <Input value={form.copper_price_usd_per_tonne} onChange={set('copper_price_usd_per_tonne')} type="number" min="0" step="1" placeholder="14304" />
                  <span style={{ position: 'absolute', right: '0.75rem', top: '50%', transform: 'translateY(-50%)', fontSize: '0.8rem', color: 'var(--color-text-muted)', pointerEvents: 'none' }}>$/t</span>
                </div>
              </Field>
            </ThreeCol>

            <ThreeCol>
              <Field label="Silver Financing / Lease Rate" required hint="Annual rate on silver inventory — bank lease rate or cost of capital">
                <div style={{ position: 'relative' }}>
                  <Input value={form.silver_lease_rate_pct_per_year} onChange={set('silver_lease_rate_pct_per_year')} type="number" min="0" step="0.1" placeholder="9" />
                  <span style={{ position: 'absolute', right: '0.75rem', top: '50%', transform: 'translateY(-50%)', fontSize: '0.8rem', color: 'var(--color-text-muted)', pointerEvents: 'none' }}>% /yr</span>
                </div>
              </Field>
              <Field label="Electricity Cost" required hint={form.country && COUNTRY_BENCHMARKS[form.country] ? `IEA regional rate for ${form.country}` : 'IEA global industrial avg'}>
                <div style={{ position: 'relative' }}>
                  <Input value={form.electricity_cost_usd_per_kwh} onChange={set('electricity_cost_usd_per_kwh')} type="number" min="0" step="0.01" placeholder="0.10" />
                  <span style={{ position: 'absolute', right: '0.75rem', top: '50%', transform: 'translateY(-50%)', fontSize: '0.8rem', color: 'var(--color-text-muted)', pointerEvents: 'none' }}>$/kWh</span>
                </div>
              </Field>
              <Field label="Labor Cost" required hint={form.country && COUNTRY_BENCHMARKS[form.country] ? `ILO/World Bank benchmark for ${form.country}` : 'ILO/World Bank global benchmark'}>
                <div style={{ position: 'relative' }}>
                  <Input value={form.labor_cost_usd_per_hour} onChange={set('labor_cost_usd_per_hour')} type="number" min="0" step="0.5" placeholder="25" />
                  <span style={{ position: 'absolute', right: '0.75rem', top: '50%', transform: 'translateY(-50%)', fontSize: '0.8rem', color: 'var(--color-text-muted)', pointerEvents: 'none' }}>$/hr</span>
                </div>
              </Field>
            </ThreeCol>

            <ThreeCol>
              <Field label="Nitric Acid Cost" required hint="Industrial grade HNO₃ benchmark">
                <div style={{ position: 'relative' }}>
                  <Input value={form.nitric_acid_cost_usd_per_ltr} onChange={set('nitric_acid_cost_usd_per_ltr')} type="number" min="0" step="0.01" placeholder="0.50" />
                  <span style={{ position: 'absolute', right: '0.75rem', top: '50%', transform: 'translateY(-50%)', fontSize: '0.8rem', color: 'var(--color-text-muted)', pointerEvents: 'none' }}>$/L</span>
                </div>
              </Field>
              <Field label="Operating Days / Year" required>
                <div style={{ position: 'relative' }}>
                  <Input value={form.operating_days_per_year} onChange={set('operating_days_per_year')} type="number" min="1" max="365" step="1" placeholder="250" />
                  <span style={{ position: 'absolute', right: '0.75rem', top: '50%', transform: 'translateY(-50%)', fontSize: '0.8rem', color: 'var(--color-text-muted)', pointerEvents: 'none' }}>days/yr</span>
                </div>
              </Field>
              <Field label="Payback Expectation" required hint="Customer's target payback period">
                <div style={{ position: 'relative' }}>
                  <Input value={form.customer_payback_years} onChange={set('customer_payback_years')} type="number" min="0.5" step="0.5" placeholder="3" />
                  <span style={{ position: 'absolute', right: '0.75rem', top: '50%', transform: 'translateY(-50%)', fontSize: '0.8rem', color: 'var(--color-text-muted)', pointerEvents: 'none' }}>yrs</span>
                </div>
              </Field>
            </ThreeCol>

            <SectionTitle>Block 3 – Existing Installation Inputs</SectionTitle>

            <TwoCol>
              <Field label="Age of Existing Moebius Cells" required hint="Enter 0 for a greenfield project">
                <div style={{ position: 'relative' }}>
                  <Input value={form.moebius_age_years} onChange={set('moebius_age_years')} type="number" min="0" step="1" placeholder="e.g. 8" />
                  <span style={{ position: 'absolute', right: '0.75rem', top: '50%', transform: 'translateY(-50%)', fontSize: '0.8rem', color: 'var(--color-text-muted)', pointerEvents: 'none' }}>yrs</span>
                </div>
              </Field>
              <Field
                label="Monthly Repair Cost"
                hint={
                  form.moebius_age_years !== '' && !isNaN(parseFloat(form.moebius_age_years))
                    ? parseFloat(form.moebius_age_years) === 0
                      ? 'Greenfield — no repair costs'
                      : `Auto-estimated for ${form.moebius_age_years} yr cell age`
                    : 'Auto-filled based on cell age'
                }
              >
                <div style={{ position: 'relative' }}>
                  <Input value={form.monthly_repair_cost_usd} onChange={set('monthly_repair_cost_usd')} type="number" min="0" step="100" placeholder="Auto-filled" />
                  <span style={{ position: 'absolute', right: '0.75rem', top: '50%', transform: 'translateY(-50%)', fontSize: '0.8rem', color: 'var(--color-text-muted)', pointerEvents: 'none' }}>$/mo</span>
                </div>
              </Field>
            </TwoCol>

            <TwoCol>
              <Field label="Downtime in Last 12 Months" hint="Unplanned / maintenance downtime">
                <div style={{ position: 'relative' }}>
                  <Input value={form.downtime_weeks_per_year} onChange={set('downtime_weeks_per_year')} type="number" min="0" max="52" step="0.5" placeholder="e.g. 2" />
                  <span style={{ position: 'absolute', right: '0.75rem', top: '50%', transform: 'translateY(-50%)', fontSize: '0.8rem', color: 'var(--color-text-muted)', pointerEvents: 'none' }}>wks/yr</span>
                </div>
              </Field>
              <Field label="Upcoming Major Spend" hint="Planned capex on existing installation">
                <div style={{ position: 'relative' }}>
                  <Input value={form.upcoming_major_spend_usd} onChange={set('upcoming_major_spend_usd')} type="number" min="0" step="1000" placeholder="e.g. 50000" />
                  <span style={{ position: 'absolute', right: '0.75rem', top: '50%', transform: 'translateY(-50%)', fontSize: '0.8rem', color: 'var(--color-text-muted)', pointerEvents: 'none' }}>USD</span>
                </div>
              </Field>
            </TwoCol>
          </>
        )}

        {/* Step 1 — Process */}
        {step === 1 && (
          <>
            {/* Derived Inputs — read-only, computed from Customer tab */}
            {(() => {
              const kgPerDay  = parseFloat(form.daily_ag_production_kg_per_day);
              const pctAg     = parseFloat(form.feed_pct_ag);
              const pctAu     = parseFloat(form.feed_pct_au);
              const pctCu     = parseFloat(form.feed_pct_cu);
              const opDays    = parseFloat(form.operating_days_per_year);
              const age       = parseFloat(form.moebius_age_years);

              const hasBase = !isNaN(kgPerDay) && !isNaN(opDays);

              const agT  = hasBase && !isNaN(pctAg) ? kgPerDay * (pctAg / 100) * opDays / 1000 : null;
              const auT  = hasBase && !isNaN(pctAu) ? kgPerDay * (pctAu / 100) * opDays / 1000 : null;
              const cuT  = hasBase && !isNaN(pctCu) ? kgPerDay * (pctCu / 100) * opDays / 1000 : null;
              const agOz = agT !== null ? agT * 1000 * 32150.75 : null;

              const moebiusCells = !isNaN(kgPerDay) ? Math.ceil(kgPerDay / 250 * 3) : null;
              const scaleFactor  = !isNaN(kgPerDay) ? kgPerDay / 50 : null;
              const moebiusFTE   = !isNaN(kgPerDay) ? Math.ceil(kgPerDay / 250 * 3) : null;
              const emewFTE      = !isNaN(kgPerDay) ? Math.max(2, Math.ceil(kgPerDay / 500)) : null;

              // Module 6 estimates — shown only when the actual Block 3 field is blank
              const showRepair   = form.monthly_repair_cost_usd === '';
              const showDowntime = form.downtime_weeks_per_year === '';
              const showCapex    = form.upcoming_major_spend_usd === '';

              let estRepair = null, estDowntime = null, estCapex = null;
              if (!isNaN(age)) {
                if (age === 0)       { estRepair = 0;    estDowntime = 0; estCapex = 0;       }
                else if (age <= 5)   { estRepair = 2000; estDowntime = 0; estCapex = 0;       }
                else if (age <= 10)  { estRepair = 3000; estDowntime = 1; estCapex = 55000;   }
                else if (age <= 15)  { estRepair = 5000; estDowntime = 3; estCapex = 1010000; }
                else                 { estRepair = 8000; estDowntime = 6; estCapex = 1200000; }
              }

              const fmtN  = (v, d = 0) => v === null ? '—' : v.toLocaleString(undefined, { minimumFractionDigits: d, maximumFractionDigits: d });
              const fmtUSD = (v) => v === null ? '—' : `$${v.toLocaleString()}`;

              const rows = [
                { label: 'Ag throughput',             formula: 'Daily Ag prod. × Feed %Ag × Op. days ÷ 1,000',         value: agT  !== null ? `${fmtN(agT,  1)} t/yr`    : '—' },
                { label: 'Au throughput',             formula: 'Daily Ag prod. × Feed %Au × Op. days ÷ 1,000',         value: auT  !== null ? `${fmtN(auT,  2)} t/yr`    : '—' },
                { label: 'Cu throughput',             formula: 'Daily Ag prod. × Feed %Cu × Op. days ÷ 1,000',         value: cuT  !== null ? `${fmtN(cuT,  2)} t/yr`    : '—' },
                { label: 'Annual Ag production',      formula: 'Ag throughput × 1,000 × 32,150.75 tr oz/kg',           value: agOz !== null ? `${fmtN(agOz, 0)} tr oz/yr` : '—' },
                { label: 'Est. Moebius cells required', formula: 'ceil(kg/day ÷ 250 × 3)  [3 cells per 250 kg/day]',    value: moebiusCells !== null ? String(moebiusCells) : '—' },
                { label: 'Scale factor vs UPMR',      formula: 'kg/day ÷ 50  [UPMR baseline = 50 kg/day]',             value: scaleFactor  !== null ? `${fmtN(scaleFactor, 1)}×` : '—' },
                { label: 'Moebius FTEs estimated',    formula: 'ceil(kg/day ÷ 250 × 3)  [3 FTE per 250 kg/day]',       value: moebiusFTE   !== null ? String(moebiusFTE)   : '—' },
                { label: 'emew FTEs estimated',       formula: 'max(2, ceil(kg/day ÷ 500))  [min 2 FTE]',              value: emewFTE      !== null ? String(emewFTE)      : '—' },
                ...(showRepair   ? [{ label: 'Est. monthly repair cost',  formula: 'Age: 0 yr→$0 · ≤5 yr→$2K · ≤10 yr→$3K · ≤15 yr→$5K · >15 yr→$8K', value: fmtUSD(estRepair)   }] : []),
                ...(showDowntime ? [{ label: 'Est. downtime (wks/yr)',    formula: 'Age: ≤5 yr→0 · ≤10 yr→1 wk · ≤15 yr→3 wks · >15 yr→6 wks',          value: estDowntime !== null ? `${estDowntime} wks` : '—' }] : []),
                ...(showCapex    ? [{ label: 'Est. replacement capex',    formula: 'Age: ≤5 yr→$0 · ≤10 yr→$55K · ≤15 yr→$1.01M · >15 yr→$1.2M',        value: fmtUSD(estCapex)   }] : []),
              ];

              return (
                <div style={{ marginBottom: '2rem' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.75rem' }}>
                    <span style={{ fontSize: '0.6875rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em', color: 'var(--color-text-muted)' }}>
                      Derived Inputs
                    </span>
                    <span style={{ fontSize: '0.7rem', color: 'var(--color-text-muted)', background: '#F7F8FA', border: '1px solid var(--color-border)', borderRadius: 4, padding: '1px 6px' }}>
                      Read-only — calculated from Customer tab
                    </span>
                  </div>
                  <div style={{ overflowX: 'auto', border: '1px solid var(--color-border)', borderRadius: 'var(--radius-md)' }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.8rem' }}>
                      <thead>
                        <tr style={{ background: '#EBF0FA' }}>
                          <th style={{ padding: '0.5rem 0.75rem', textAlign: 'left', fontWeight: 700, color: 'var(--color-dark-blue)' }}>Parameter</th>
                          <th style={{ padding: '0.5rem 0.75rem', textAlign: 'right', fontWeight: 700, color: 'var(--color-dark-blue)', width: 150, whiteSpace: 'nowrap' }}>Value</th>
                          <th style={{ padding: '0.5rem 0.75rem', textAlign: 'left', fontWeight: 700, color: 'var(--color-dark-blue)' }}>Formula / Source</th>
                        </tr>
                      </thead>
                      <tbody>
                        {rows.map((r, i) => (
                          <tr key={r.label} style={{ borderTop: '1px solid var(--color-border)', background: i % 2 === 0 ? '#fff' : '#FAFBFD' }}>
                            <td style={{ padding: '0.4rem 0.75rem', fontWeight: 500, color: 'var(--color-text-primary)' }}>{r.label}</td>
                            <td style={{ padding: '0.4rem 0.75rem', textAlign: 'right', fontWeight: 700, color: r.value === '—' ? 'var(--color-text-muted)' : 'var(--color-blue)', whiteSpace: 'nowrap' }}>{r.value}</td>
                            <td style={{ padding: '0.4rem 0.75rem', color: 'var(--color-text-muted)', fontStyle: 'italic', fontSize: '0.775rem' }}>{r.formula}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              );
            })()}
            {/* Verified Constants */}
            <div style={{ marginTop: '2rem' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '1rem' }}>
                <span style={{ fontSize: '0.6875rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em', color: 'var(--color-text-muted)' }}>
                  Verified Constants
                </span>
                {!user?.isAdmin && (
                  <span title="View only — admin access required to edit" style={{ fontSize: '0.75rem', color: 'var(--color-text-muted)' }}>🔒</span>
                )}
                {user?.isAdmin && (
                  <span style={{ fontSize: '0.7rem', color: 'var(--color-blue)', background: '#EBF0FA', borderRadius: 4, padding: '1px 6px', fontWeight: 600 }}>Admin — click ✎ to edit</span>
                )}
              </div>
              {constants.length === 0 ? (
                <p style={{ fontSize: '0.8125rem', color: 'var(--color-text-muted)' }}>Loading constants…</p>
              ) : (
                <div style={{ overflowX: 'auto', border: '1px solid var(--color-border)', borderRadius: 'var(--radius-md)' }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.8rem' }}>
                    <thead>
                      <tr style={{ background: '#EBF0FA' }}>
                        <th style={{ padding: '0.5rem 0.75rem', textAlign: 'left', fontWeight: 700, color: 'var(--color-dark-blue)', width: 28, whiteSpace: 'nowrap' }}>#</th>
                        <th style={{ padding: '0.5rem 0.75rem', textAlign: 'left', fontWeight: 700, color: 'var(--color-dark-blue)' }}>Parameter</th>
                        <th style={{ padding: '0.5rem 0.75rem', textAlign: 'right', fontWeight: 700, color: 'var(--color-dark-blue)', width: 110, whiteSpace: 'nowrap' }}>Value</th>
                        <th style={{ padding: '0.5rem 0.75rem', textAlign: 'left', fontWeight: 700, color: 'var(--color-dark-blue)' }}>Source</th>
                        {user?.isAdmin && <th style={{ width: 64 }}></th>}
                      </tr>
                    </thead>
                    <tbody>
                      {constants.map((c, i) => (
                        <tr key={c.key} style={{ borderTop: '1px solid var(--color-border)', background: i % 2 === 0 ? '#fff' : '#FAFBFD' }}>
                          <td style={{ padding: '0.4rem 0.75rem', color: 'var(--color-text-muted)' }}>{c.sort_order}</td>
                          <td style={{ padding: '0.4rem 0.75rem', fontWeight: 500, color: 'var(--color-text-primary)' }}>{c.label}</td>
                          <td style={{ padding: '0.4rem 0.75rem', textAlign: 'right', fontWeight: 700, color: 'var(--color-blue)', whiteSpace: 'nowrap' }}>
                            {editingKey === c.key ? (
                              <input
                                type="number"
                                value={editValue}
                                onChange={e => setEditValue(e.target.value)}
                                onKeyDown={e => {
                                  if (e.key === 'Enter') saveConstant(c.key);
                                  if (e.key === 'Escape') setEditingKey(null);
                                }}
                                style={{ width: 90, textAlign: 'right', padding: '2px 6px', fontSize: '0.8rem', border: '1px solid var(--color-blue)', borderRadius: 4, outline: 'none' }}
                                autoFocus
                              />
                            ) : (
                              c.is_pct
                                ? `${Number(c.value).toFixed(2)}%`
                                : Number(c.value).toLocaleString(undefined, { maximumFractionDigits: 2 })
                            )}
                          </td>
                          <td style={{ padding: '0.4rem 0.75rem', color: 'var(--color-text-muted)', fontStyle: 'italic', fontSize: '0.775rem' }}>{c.source}</td>
                          {user?.isAdmin && (
                            <td style={{ padding: '0.4rem 0.5rem', textAlign: 'center', whiteSpace: 'nowrap' }}>
                              {editingKey === c.key ? (
                                <>
                                  <button
                                    onClick={() => saveConstant(c.key)}
                                    disabled={savingKey === c.key}
                                    title="Save"
                                    style={{ background: 'var(--color-green)', color: '#fff', border: 'none', borderRadius: 4, padding: '2px 7px', cursor: 'pointer', fontSize: '0.8rem', marginRight: 3 }}
                                  >✓</button>
                                  <button
                                    onClick={() => setEditingKey(null)}
                                    title="Cancel"
                                    style={{ background: 'var(--color-border)', color: 'var(--color-text-primary)', border: 'none', borderRadius: 4, padding: '2px 7px', cursor: 'pointer', fontSize: '0.8rem' }}
                                  >✕</button>
                                </>
                              ) : (
                                <button
                                  onClick={() => { setEditingKey(c.key); setEditValue(String(c.value)); }}
                                  title="Edit"
                                  style={{ background: 'none', border: '1px solid var(--color-border)', borderRadius: 4, padding: '2px 7px', cursor: 'pointer', fontSize: '0.8rem', color: 'var(--color-text-muted)' }}
                                >✎</button>
                              )}
                            </td>
                          )}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </>
        )}

        {/* Step 2 — Economics */}
        {step === 2 && (
          <>
            <TwoCol>
              <Field label="Silver spot price" required hint="Current market price">
                <div style={{ position: 'relative' }}>
                  <Input value={form.silver_price_usd_per_troy_oz} onChange={set('silver_price_usd_per_troy_oz')} type="number" min="0" step="0.01" placeholder="32" />
                  <span style={{ position: 'absolute', right: '0.75rem', top: '50%', transform: 'translateY(-50%)', fontSize: '0.8rem', color: 'var(--color-text-muted)' }}>$/tr oz</span>
                </div>
              </Field>
              <Field label="Estimated system investment" hint="Optional — enables payback / ROI calculation">
                <div style={{ position: 'relative' }}>
                  <Input value={form.capex_usd} onChange={set('capex_usd')} type="number" min="0" step="1000" placeholder="Optional" />
                  <span style={{ position: 'absolute', right: '0.75rem', top: '50%', transform: 'translateY(-50%)', fontSize: '0.8rem', color: 'var(--color-text-muted)' }}>USD</span>
                </div>
              </Field>
            </TwoCol>
            <Field label="Notes">
              <textarea
                value={form.notes}
                onChange={e => set('notes')(e.target.value)}
                placeholder="Additional context, assumptions, or comments..."
                rows={3}
                style={{ ...inputStyle, resize: 'vertical', lineHeight: 1.5 }}
              />
            </Field>

            {/* Summary preview */}
            <div style={{ background: '#EBF0FA', borderRadius: 'var(--radius-md)', padding: '1rem', marginTop: '0.5rem' }}>
              <p style={{ fontSize: '0.8125rem', fontWeight: 600, color: 'var(--color-dark-blue)', marginBottom: '0.5rem' }}>Assessment summary</p>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.25rem 1rem', fontSize: '0.8125rem', color: 'var(--color-text-secondary)' }}>
                <span>Company: <strong style={{ color: 'var(--color-text-primary)' }}>{form.customer_name}</strong></span>
                <span>Country: <strong style={{ color: 'var(--color-text-primary)' }}>{form.country || '—'}</strong></span>
                <span>Daily Ag production: <strong style={{ color: 'var(--color-text-primary)' }}>{form.daily_ag_production_kg_per_day} kg/day</strong></span>
                <span>Feed: <strong style={{ color: 'var(--color-text-primary)' }}>{form.feed_pct_ag}% Ag · {form.feed_pct_cu}% Cu · {form.feed_pct_au}% Au</strong></span>
              </div>
            </div>
          </>
        )}

        {/* Navigation */}
        <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '2rem' }}>
          <button
            className="btn btn-outline"
            onClick={() => step === 0 ? navigate('/assessment/new') : setStep(s => s - 1)}
          >
            {step === 0 ? '← Cancel' : '← Back'}
          </button>
          {step < 2 ? (
            <button className="btn btn-primary" onClick={next}>
              Continue →
            </button>
          ) : (
            <button className="btn btn-primary" onClick={submit} disabled={submitting}>
              {submitting ? <><span className="spinner" style={{ marginRight: 6 }} />Calculating…</> : 'Generate Assessment →'}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
